import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import './styles.scss';
import PropTypes from 'prop-types';
import {
    getUserPermissionsKeys,
    getPartnerList,
    getPartnerListLoading,
    getViewAsPartner,
    getSelectedPartner,
    getRefreshingToken,
    getHasMorePartners,
} from '../../screens/auth/selectors';
import {
    getPartnerListRequest,
    getPartnerTokenRequest,
    getRefreshTokenRequest,
    resetViewAs,
    setSelectedPartner,
} from '../../screens/auth/actions';
import { triggerToastOn } from '../redux/actions';
import { PARTNER_TOKEN_GENERATE } from '../../screens/auth/constants';
import LoadingComponent from '../LoadingComponent';
import AutoCompleteSingle from '../AutoCompleteSingle';
import Toast from '../Toast';
import { noop } from '../propTypes';
import qs from 'query-string';
import { useLocation } from 'react-router';
import usePrevious from '../../common/hooks/usePrevious';

function ViewAsPartner({
    permissions,
    partners,
    getPartners,
    getPartnerToken,
    getRefreshToken,
    setSelectedPartner,
    viewAsPartner,
    selectedPartner,
    isLoading,
    resetValues,
    partnersLoading,
    triggerToast,
}) {
    const hasPermission =
        permissions.length && permissions.includes(PARTNER_TOKEN_GENERATE);
    // checking if user has permission to view as partner or viewAsPartner is already active
    const hasPermissionOrViewAsPartner = hasPermission || viewAsPartner;
    const location = useLocation();
    const queryParams = qs.parse(location.search);

    useEffect(() => {
        resetValues();
        if (hasPermissionOrViewAsPartner) getPartners();
    }, []);
    const prevPartnersLoading = usePrevious(partnersLoading);

    useEffect(() => {
        //check if
        // partnerId is coming in query params
        // previously partners were loading and now is loading is false
        if (queryParams.partnerId) {
            if (hasPermissionOrViewAsPartner) {
                if (viewAsPartner) {
                    triggerToast('Switch to partner is already active');
                } else if (prevPartnersLoading && !partnersLoading) {
                    const partner = partners.find(
                        (p) => p.id == queryParams.partnerId,
                    );
                    if (partner) {
                        setSelectedPartner(partner);
                        getPartnerToken(partner.id);
                    } else {
                        triggerToast('Invalid partner ID');
                    }
                }
            } else {
                triggerToast('Permission denied for switch to partner');
            }
        }
    }, [partnersLoading]);

    const handleViewAsEnter = () => {
        getPartnerToken(selectedPartner.id);
    };

    const handleViewAsExit = () => {
        getRefreshToken();
    };

    const loader = (
        <div className="buttonLoader">
            <LoadingComponent isLoading={true} />
        </div>
    );

    const searchContainer = (
        <div className="search-container">
            <AutoCompleteSingle
                id="partnerId-autocomplete"
                isLoading={partnersLoading}
                options={partners.map((p) => ({ id: p.id, name: p.name }))}
                onSelect={(partner) => {
                    setSelectedPartner(partner);
                }}
                defaultValue={
                    selectedPartner && selectedPartner.id
                        ? selectedPartner
                        : null
                }
                placeholder={'Select Partner'}
                disabled={viewAsPartner}
                loadMoreItem={() => {
                    if (!partnersLoading) getPartners();
                }}
                getOptionLabel={(option) => `${option.id} | ${option.name}`}
                style={{ width: 300 }}
                value={selectedPartner}
            />
            {isLoading ? (
                loader
            ) : viewAsPartner ? (
                <button
                    onClick={handleViewAsExit}
                    disabled={!(selectedPartner && selectedPartner.id)}
                    className="btn btn-custom btn-exit"
                >
                    Exit
                </button>
            ) : (
                <button
                    onClick={handleViewAsEnter}
                    disabled={!(selectedPartner && selectedPartner.id)}
                    className="btn btn-custom"
                >
                    View
                </button>
            )}
        </div>
    );

    const toast = <Toast />;
    return hasPermissionOrViewAsPartner ? (
        <>
            {searchContainer}
            {toast}
        </>
    ) : (
        toast
    );
}

ViewAsPartner.propTypes = {
    permissions: PropTypes.array,
    partners: PropTypes.array,
    partnersLoading: PropTypes.bool,
    getPartners: PropTypes.func,
    getPartnerToken: PropTypes.func,
    getRefreshToken: PropTypes.func,
    viewAsPartner: PropTypes.bool,
    setSelectedPartner: PropTypes.func,
    resetValues: PropTypes.func,
    selectedPartner: PropTypes.object,
    isLoading: PropTypes.bool,
    loadMore: PropTypes.bool,
    triggerToast: PropTypes.func,
};

ViewAsPartner.defaultProps = {
    getPartners: noop,
    partners: [],
};

const mapStateToProps = (state) => {
    return {
        permissions: getUserPermissionsKeys(state),
        partners: getPartnerList(state),
        partnersLoading: getPartnerListLoading(state),
        viewAsPartner: getViewAsPartner(state),
        selectedPartner: getSelectedPartner(state),
        isLoading: getRefreshingToken(state),
        loadMore: getHasMorePartners(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getPartners: () => {
            dispatch(getPartnerListRequest());
        },
        getPartnerToken: (partnerId) => {
            dispatch(getPartnerTokenRequest(partnerId));
        },
        getRefreshToken: () => {
            dispatch(getRefreshTokenRequest());
        },
        setSelectedPartner: (partner) => {
            dispatch(setSelectedPartner(partner));
        },
        resetValues: () => {
            dispatch(resetViewAs());
        },
        triggerToast: (toastMessage) => {
            dispatch(triggerToastOn({ toastMessage, toastType: 'error' }));
        },
    };
};

const ConnectedViewAsPartner = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ViewAsPartner);
export default ConnectedViewAsPartner;
